import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlayIcon } from 'lucide-react';
import { getProductBySlug } from '../data/products';
import { ProductSpecs } from '../components/products/ProductSpecs';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useTranslation } from '../lib/i18n';
import { formatEur } from '../lib/format';
import { trackProductViewed, trackProductSpecsViewed, trackProductGalleryViewed } from '../lib/pixel';
// Fires a tracking callback once when the element scrolls into view.
function useViewedOnce(enabled: boolean, onView: () => void): React.RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || firedRef.current) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          onView();
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.3
    });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  return ref;
}
interface ProductDetailPageProps {
  onRequestQuote: (productId: string) => void;
}
export function ProductDetailPage({
  onRequestQuote
}: ProductDetailPageProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProductBySlug(slug) : undefined;
  const [activeMedia, setActiveMedia] = useState<{
    type: 'image' | 'video';
    url: string;
  } | null>(null);
  const productCtx = {
    product_name: product?.name,
    product_category: product?.series
  };
  // ProductViewed once per product page load.
  useEffect(() => {
    if (product) trackProductViewed(productCtx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);
  // ProductSpecsViewed / ProductGalleryViewed when those sections scroll in.
  const specsRef = useViewedOnce(!!product, () => trackProductSpecsViewed(productCtx));
  const galleryRef = useViewedOnce(!!(product && product.gallery && product.gallery.length > 0), () => trackProductGalleryViewed(productCtx));
  if (!product) {
    return <Navigate to="/products" replace data-id="element-944" />;
  }
  const hasGallery = product.gallery && product.gallery.length > 0;
  return <main className="min-h-screen bg-dark-bg" data-id="element-945">
      {/* Back Link */}
      <div className="bg-dark-surface border-b-2 border-dark-border" data-id="element-946">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-947">
          <Link to="/products" className="inline-flex items-center text-gray-400 hover:text-brand-yellow transition-colors focus:outline-none focus:ring-2 focus:ring-brand-yellow rounded-industrial" data-id="element-948">
            <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-949" />
            {en ? 'Back to products' : ru ? 'Назад к продуктам' : 'Nazad na proizvode'}
          </Link>
        </div>
      </div>

      {/* Product Hero */}
      <section className="py-12 bg-dark-surface border-b-2 border-dark-border" data-id="element-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-951">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-952">
            <div className="space-y-4" data-id="element-953">
              <div className="w-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-industrial p-8 flex items-center justify-center min-h-[400px] lg:min-h-[500px] relative" data-id="element-954">
                <img src={product.image} alt={`${product.name} ${product.model}`} className="w-full h-auto object-contain max-h-[450px]" data-id="element-955" />
              </div>

              {/* Gallery Thumbnails */}
              {hasGallery && <div className="mt-6" ref={galleryRef} data-id="element-956">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-957">
                    {en ? 'Gallery' : ru ? 'Галерея' : 'Galerija'}
                  </h3>
                  <div className="grid grid-cols-4 gap-4" data-id="element-958">
                    {product.gallery?.map((media, idx) => <button key={idx} onClick={() => setActiveMedia(media)} className="relative aspect-square bg-dark-bg border-2 border-dark-border rounded-industrial overflow-hidden hover:border-brand-yellow transition-colors group" data-id="element-959">
                        {media.type === 'image' ? <img src={media.url} alt="Gallery item" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-960" /> : <div className="w-full h-full flex items-center justify-center bg-gray-900 group-hover:bg-gray-800 transition-colors" data-id="element-961">
                            <PlayIcon className="w-8 h-8 text-brand-yellow" data-id="element-962" />
                          </div>}
                      </button>)}
                  </div>
                </div>}
            </div>

            <div className="pt-4" data-id="element-963">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2" data-id="element-964">
                {product.name}
              </h1>
              <p className="text-2xl text-gray-400 mb-6" data-id="element-965">{product.model}</p>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-966">
                {en ? product.descriptionEn || product.description : ru ? product.descriptionRu || product.description : product.description}
              </p>
              <div className="mb-8" data-id="element-967">
                <div className="flex items-baseline gap-3" data-id="element-968">
                  <span className="text-5xl font-black text-brand-yellow" data-id="element-969">
                    €{formatEur(product.price)}
                  </span>
                  <span className="text-base text-gray-400" data-id="element-970">
                    {en ? 'excl. VAT' : ru ? 'без НДС' : 'bez PDV'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-971">
                  {en ? 'incl. VAT' : ru ? 'с НДС' : 'Sa PDV'}: €
                  {formatEur(product.price * 1.2)} ·
                  {en ? ' Availability on request.' : ru ? ' Наличие по запросу.' : ' Dostupnost na upit.'}
                </p>
              </div>
              <Button variant="primary" size="lg" onClick={() => onRequestQuote(product.id)} data-id="element-972">
                {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-dark-bg" data-id="element-973">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-974">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-975">
            {en ? 'Key features' : ru ? 'Ключевые особенности' : 'Ključne karakteristike'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-976">
            {(en && product.highlightsEn ? product.highlightsEn : ru && product.highlightsRu ? product.highlightsRu : product.highlights).map(highlight => <div key={highlight} className="flex items-center space-x-3 bg-dark-surface border-2 border-dark-border rounded-industrial p-4" data-id="element-977">
                <CheckIcon className="w-6 h-6 text-brand-yellow flex-shrink-0" data-id="element-978" />
                <span className="text-white font-semibold" data-id="element-979">{highlight}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-12 bg-dark-surface" ref={specsRef} data-id="element-980">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-981">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-982">
            {en ? 'Technical specifications' : ru ? 'Технические характеристики' : 'Tehničke specifikacije'}
          </h2>
          <ProductSpecs specs={product.specs} data-id="element-983" />
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-dark-bg" data-id="element-984">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-985">
          <h2 className="text-3xl font-black text-white mb-6" data-id="element-986">
            {en ? 'Features and benefits' : ru ? 'Функции и преимущества' : 'Funkcije i prednosti'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-987">
            {(en && product.featuresEn ? product.featuresEn : ru && product.featuresRu ? product.featuresRu : product.features).map(feature => <div key={feature} className="flex items-start space-x-3 bg-dark-surface border-2 border-dark-border rounded-industrial p-6" data-id="element-988">
                <CheckIcon className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-989" />
                <span className="text-gray-300" data-id="element-990">{feature}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-surface border-t-2 border-dark-border" data-id="element-991">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-992">
          <h2 className="text-4xl font-black text-white mb-4" data-id="element-993">
            {en ? 'Ready to order?' : ru ? 'Готовы заказать?' : 'Spremni za poručivanje?'}
          </h2>
          <p className="text-xl text-gray-400 mb-8" data-id="element-994">
            {en ? 'Contact us for a detailed quote and lead time.' : ru ? 'Свяжитесь с нами для получения подробного предложения и сроков поставки.' : 'Kontaktirajte nas za detaljnu ponudu i rok isporuke.'}
          </p>
          <Button variant="primary" size="lg" onClick={() => onRequestQuote(product.id)} data-id="element-995">
            {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
          </Button>
        </div>
      </section>

      {/* Media Modal */}
      <Modal isOpen={!!activeMedia} onClose={() => setActiveMedia(null)} title={product.name} data-id="element-996">
        <div className="bg-dark-bg rounded-industrial overflow-hidden flex items-center justify-center min-h-[50vh]" data-id="element-997">
          {activeMedia?.type === 'image' ? <img src={activeMedia.url} alt="Gallery full" className="max-w-full max-h-[80vh] object-contain" data-id="element-998" /> : activeMedia?.type === 'video' ? <video src={activeMedia.url} controls autoPlay className="max-w-full max-h-[80vh]" data-id="element-999" /> : null}
        </div>
      </Modal>
    </main>;
}